import { useRef } from 'react';

interface LineNumberedEditorProps {
  value: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  rows?: number;
  readOnly?: boolean;
  darkMode?: boolean;
}

export default function LineNumberedEditor({
  value,
  onChange,
  placeholder,
  rows = 12,
  readOnly = false,
  darkMode = false,
}: LineNumberedEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const lineNumbersRef = useRef<HTMLDivElement>(null);

  const lineCount = Math.max(rows, value.split('\n').length);
  const lineNumbers = Array.from({ length: lineCount }, (_, i) => i + 1);

  // Sync scroll between line numbers and textarea
  const handleScroll = () => {
    if (lineNumbersRef.current && textareaRef.current) {
      lineNumbersRef.current.scrollTop = textareaRef.current.scrollTop;
    }
  };

  const editorBg   = darkMode ? '#181818' : '#FFFFFF';
  const readOnlyBg = darkMode ? '#383838' : '#F2F2F2';
  const textColor  = darkMode ? '#FFFFFF' : '#000000';
  const lineNumBg  = darkMode ? '#1e1e1e' : '#f5f5f5';
  const lineNumColor = darkMode ? '#6e7681' : '#999999';
  const borderColor  = darkMode ? '#444' : '#ccc';

  const bg = readOnly ? readOnlyBg : editorBg;

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    border: `1px solid ${borderColor}`,
    borderRadius: '4px',
    overflow: 'hidden',
    fontFamily: 'monospace',
    fontSize: '13px',
    lineHeight: '1.5',
    width: '100%',
  };

  const lineNumbersStyle: React.CSSProperties = {
    backgroundColor: lineNumBg,
    color: lineNumColor,
    padding: '8px 8px',
    textAlign: 'right',
    userSelect: 'none',
    overflowY: 'hidden',
    minWidth: '40px',
    borderRight: `1px solid ${borderColor}`,
  };

  const textareaStyle: React.CSSProperties = {
    flex: 1,
    backgroundColor: bg,
    color: textColor,
    border: 'none',
    outline: 'none',
    padding: '8px',
    resize: 'vertical',
    fontFamily: 'monospace',
    fontSize: '13px',
    lineHeight: '1.5',
    minHeight: `${rows * 1.5 * 13 + 16}px`,
    overflowY: 'auto',
  };

  return (
    <div style={containerStyle}>
      <div ref={lineNumbersRef} style={lineNumbersStyle}>
        {lineNumbers.map((n) => (
          <div key={n}>{n}</div>
        ))}
      </div>
      <textarea
        ref={textareaRef}
        value={value}
        onChange={readOnly ? undefined : (e) => onChange?.(e.target.value)}
        onScroll={handleScroll}
        placeholder={placeholder}
        readOnly={readOnly}
        style={textareaStyle}
        spellCheck={false}
      />
    </div>
  );
}
